/**
 * Funciones tipadas de los endpoints /auth/*.
 *
 * Espejo de backend/app/blueprints/auth/routes.py.
 * Si el backend cambia el shape, ajustar tipos aquí también.
 */

import { apiFetch, tokenStorage } from "./client";
import type { AuthSuccess, User } from "@/types/api";

export interface RegisterPayload {
  email: string;
  password: string;
  full_name?: string;
  /**
   * Sprint 7 Bloque B1: beta privada — invite_code obligatorio.
   * El backend lo normaliza a MAYÚSCULAS antes del lookup, así que
   * podemos enviarlo en cualquier case (de hecho el input UI lo
   * uppercase para que el usuario vea el código tal como existe).
   */
  invite_code: string;
  /**
   * Sprint 14 S14.3 (R2 GDPR): consentimiento explícito de Términos +
   * Política. El backend exige `true` (400 terms_not_accepted si falta).
   */
  accept_terms: boolean;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface ConfirmResetPayload {
  token: string;
  new_password: string;
}

/** POST /auth/register — crea cuenta + emite par de tokens. */
export async function register(payload: RegisterPayload): Promise<AuthSuccess> {
  const data = await apiFetch<AuthSuccess>("/auth/register", {
    method: "POST",
    body: payload,
    anonymous: true,
  });
  tokenStorage.set(data.access_token, data.refresh_token);
  return data;
}

/** POST /auth/login — verifica credenciales + emite par de tokens. */
export async function login(payload: LoginPayload): Promise<AuthSuccess> {
  const data = await apiFetch<AuthSuccess>("/auth/login", {
    method: "POST",
    body: payload,
    anonymous: true,
  });
  tokenStorage.set(data.access_token, data.refresh_token);
  return data;
}

/**
 * POST /auth/oauth/google — login/registro con Google Sign-In.
 *
 * Recibe el id_token JWT que el frontend obtuvo de Google (vía
 * @react-oauth/google). El backend verifica firma, audience y
 * email_verified, y resuelve usuario en 3 pasos:
 *   1. Si User.google_id == sub: login normal.
 *   2. Si email coincide con un User nativo: vincula google_id sin pedir
 *      confirmación (Google ya verificó el email).
 *   3. Si no: crea User nuevo con email_verified_at=now.
 *
 * Sprint 7 Bloque B1: si el caller está en flujo de signup (página de
 * registro), pasa `inviteCode`. El backend solo lo exige cuando el
 * email NO existía — en signin (casos 1 y 2) lo ignora. En signin no
 * pasar inviteCode es OK; en signup sin él el backend devolverá
 * `invite_code_required`.
 */
export async function googleSignIn(
  idToken: string,
  inviteCode?: string,
  acceptTerms?: boolean,
): Promise<AuthSuccess> {
  const body: {
    id_token: string;
    invite_code?: string;
    accept_terms?: boolean;
  } = {
    id_token: idToken,
  };
  if (inviteCode) body.invite_code = inviteCode;
  // S14.3 (R2 GDPR): solo se manda en signup (el backend lo ignora en
  // signin de user existente). undefined → no se envía.
  if (acceptTerms !== undefined) body.accept_terms = acceptTerms;
  const data = await apiFetch<AuthSuccess>("/auth/oauth/google", {
    method: "POST",
    body,
    anonymous: true,
  });
  tokenStorage.set(data.access_token, data.refresh_token);
  return data;
}

/**
 * POST /auth/oauth/apple — login/registro con Sign in with Apple.
 *
 * Recibe el id_token JWT que el frontend obtuvo de Apple (vía
 * `react-apple-signin-auth` envolviendo el JS SDK oficial). El backend
 * verifica firma RS256 contra JWKS de Apple, audience (Service ID) y
 * email_verified, y resuelve usuario igual que Google.
 *
 * `name` (firstName + lastName) SOLO viene en el primer login del
 * usuario. Se manda al backend para popular `User.full_name` si está
 * vacío. En logins posteriores, este parámetro es undefined y el backend
 * deja `full_name` intacto.
 */
export async function appleSignIn(
  idToken: string,
  name?: { firstName: string; lastName: string },
  acceptTerms?: boolean,
): Promise<AuthSuccess> {
  const body: {
    id_token: string;
    name?: typeof name;
    accept_terms?: boolean;
  } = { id_token: idToken };
  if (name) body.name = name;
  // S14.3 (R2 GDPR): solo se manda en signup (el backend lo ignora en
  // signin de user existente). undefined → no se envía.
  if (acceptTerms !== undefined) body.accept_terms = acceptTerms;
  const data = await apiFetch<AuthSuccess>("/auth/oauth/apple", {
    method: "POST",
    body,
    anonymous: true,
  });
  tokenStorage.set(data.access_token, data.refresh_token);
  return data;
}

/**
 * POST /auth/logout — revoca el refresh token actual + limpia storage local.
 * Idempotente: siempre limpia local, aunque el server responda error.
 */
export async function logout(): Promise<void> {
  const refresh = tokenStorage.getRefresh();
  try {
    if (refresh) {
      await apiFetch<void>("/auth/logout", {
        method: "POST",
        body: { refresh_token: refresh },
      });
    }
  } finally {
    tokenStorage.clear();
  }
}

/** GET /auth/me — devuelve el user autenticado. */
export async function me(): Promise<{ user: User }> {
  return apiFetch<{ user: User }>("/auth/me");
}

/**
 * Sprint 14 S14.5 (R3 GDPR): payload del borrado de cuenta.
 * - Usuario nativo: `password` (reautenticación).
 * - Usuario solo-OAuth: `confirm: true` (confirmación explícita).
 */
export interface DeleteAccountPayload {
  password?: string;
  confirm?: boolean;
}

/**
 * DELETE /auth/account — borra la cuenta y TODOS los datos del usuario.
 * IRREVERSIBLE. Tras éxito limpia el storage local de tokens.
 */
export async function deleteAccount(
  payload: DeleteAccountPayload,
): Promise<void> {
  await apiFetch<void>("/auth/account", {
    method: "DELETE",
    body: payload,
  });
  tokenStorage.clear();
}

/** POST /auth/reset-password — solicita email de reset (anti-enumeration: 202 siempre). */
export async function requestReset(email: string): Promise<void> {
  await apiFetch<{ status: string }>("/auth/reset-password", {
    method: "POST",
    body: { email },
    anonymous: true,
  });
}

/** POST /auth/reset-password/confirm — consume token + cambia password. */
export async function confirmReset(payload: ConfirmResetPayload): Promise<void> {
  await apiFetch<{ status: string }>("/auth/reset-password/confirm", {
    method: "POST",
    body: payload,
    anonymous: true,
  });
}
